import { ErrorMessages } from '@/consts/errors';

import type { GenerationErrorType } from '@/types';

export type GenerationError = {
  userMessage: string;
  reason: string;
  errorType: GenerationErrorType;
};

const hasMessage = (error: unknown): error is { message: string } =>
  error !== null &&
  typeof error === 'object' &&
  'message' in error &&
  typeof error.message === 'string';

const hasReason = (error: unknown): error is { reason: string } =>
  error !== null &&
  typeof error === 'object' &&
  'reason' in error &&
  typeof error.reason === 'string';

const hasStatusCode = (error: unknown): error is { statusCode: number } =>
  error !== null &&
  typeof error === 'object' &&
  'statusCode' in error &&
  typeof error.statusCode === 'number';

/**
 * Parse generation errors and return user-friendly message + real reason for analytics.
 */
const getGenerationErrorMessage = (error: unknown): GenerationError => {
  const message = hasMessage(error) ? error.message : '';
  const reason = hasReason(error) ? error.reason : '';
  const errorMessage = message.toLowerCase();
  const statusCode = hasStatusCode(error) ? error.statusCode : null;
  const statusPrefix = statusCode ? `[${statusCode}] ` : '';

  // Content moderation
  if (errorMessage.includes('prohibited content')) {
    return {
      userMessage: ErrorMessages.prohibitedContent,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'content_moderation',
    };
  }

  // Timeout
  if (
    errorMessage.includes('timeout') ||
    errorMessage.includes('maximum attempts')
  ) {
    return {
      userMessage: ErrorMessages.generationTimeout,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'timeout',
    };
  }

  // Rate limiting (429)
  if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
    return {
      userMessage: ErrorMessages.rateLimited,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'rate_limited',
    };
  }

  // Server errors (5xx)
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('502') ||
    errorMessage.includes('503') ||
    errorMessage.includes('504') ||
    errorMessage.includes('service unavailable')
  ) {
    return {
      userMessage: ErrorMessages.serverError,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'server_error',
    };
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection')
  ) {
    return {
      userMessage: ErrorMessages.networkError,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'network_error',
    };
  }

  // No result URL
  if (errorMessage.includes('no result url')) {
    return {
      userMessage: ErrorMessages.noResultUrl,
      reason: statusPrefix + (message || reason || 'unknown'),
      errorType: 'no_result',
    };
  }

  // 422 - Extract actual error message from magic flow wrapper
  if (errorMessage.includes('422')) {
    const magicFlowPrefix =
      /MAGIC_FLOW_WEBHOOK failed with message: magic_flow_orchestrator_failed - MAGIC_FLOW_ORCHESTRATOR failed with message:\s*/i;
    const match = message.match(magicFlowPrefix);
    if (match && match.index !== undefined) {
      // Backend appends ` - {"usage":...}` billing metadata; keep it only in `reason`
      const cleanMessage = message
        .slice(match.index + match[0].length)
        .replace(/\s+-\s+\{[\s\S]*\}\s*$/, '')
        .trim();
      if (cleanMessage) {
        return {
          userMessage: cleanMessage,
          reason: statusPrefix + (message || reason || 'unknown'),
          errorType: 'general',
        };
      }
    }
  }

  return {
    userMessage: ErrorMessages.generationFailed,
    reason: statusPrefix + (message || reason || 'unknown'),
    errorType: 'general',
  };
};

export default getGenerationErrorMessage;
